import React, { useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, QrCode, Printer } from 'lucide-react';

export const QRCodeCard = ({ matricule, collaboratorName }) => {
  const qrRef = useRef(null);

  if (!matricule) {
    return null;
  }

  const handleDownload = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      
      const pngFile = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.download = `qr-${matricule}.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const svg = qrRef.current?.querySelector('svg');
    if (!svg || !printWindow) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Badge - ${collaboratorName}</title>
          <style>
            body {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              margin: 0;
              font-family: Arial, sans-serif;
            }
            .badge {
              border: 2px solid #1e40af;
              border-radius: 16px;
              padding: 24px;
              text-align: center;
              width: 300px;
            }
            .name {
              font-size: 20px;
              font-weight: bold;
              margin-bottom: 8px;
              color: #1f2937;
            }
            .matricule {
              font-size: 14px;
              color: #6b7280;
              margin-bottom: 16px;
            }
            .qr-container {
              display: flex;
              justify-content: center;
              margin-bottom: 16px;
            }
            .footer {
              font-size: 12px;
              color: #9ca3af;
            }
            @media print {
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          <div class="badge">
            <div class="name">${collaboratorName}</div>
            <div class="matricule">Matricule: ${matricule}</div>
            <div class="qr-container">${svgData}</div>
            <div class="footer">Scanner pour connexion rapide</div>
          </div>
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() { window.close(); }
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <QrCode className="w-5 h-5" />
          QR Code de connexion
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center gap-4">
          <div 
            ref={qrRef}
            className="p-4 bg-white rounded-lg border-2 border-dashed border-gray-200"
          >
            <QRCodeSVG 
              value={matricule}
              size={180}
              level="H"
              includeMargin={true}
              bgColor="#ffffff"
              fgColor="#1e40af"
            />
          </div>
          
          <div className="text-center">
            <p className="text-sm font-medium text-gray-900">Matricule: {matricule}</p>
            <p className="text-xs text-muted-foreground mt-1">
              Scannez ce code pour vous connecter rapidement
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button 
              onClick={handleDownload} 
              variant="outline" 
              size="sm"
              className="rounded-full"
            >
              <Download className="w-4 h-4 mr-2" />
              Télécharger
            </Button>
            <Button 
              onClick={handlePrint} 
              variant="outline" 
              size="sm"
              className="rounded-full"
            >
              <Printer className="w-4 h-4 mr-2" />
              Imprimer Badge
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default QRCodeCard;
